import subductionWasmUrl from "@automerge/automerge-subduction/wasm?url";
import automergeWasmUrl from "@automerge/automerge/automerge.wasm?url";
import { useRepo } from "@automerge/react";
import type { AppLaunchSpec } from "forest/sandbox/app-host";
import {
  AppHostRegistryProvider,
  type RegistryHostConfig,
} from "forest/sandbox/AppHostRegistry";
import type { CapabilityProviders } from "playground/apps/capabilities";
import {
  workspaceProgramPath,
  workspaceProgramSpecifier,
} from "playground/apps/loader/paths";
import { normalizeAppIntegrations } from "playground/apps/manifest";
import type { Playground } from "playground/hal/index";
import { type ReactNode, useMemo } from "react";
import { invariant } from "ts-invariant";

import { useBearSandbox } from "@/components/BearSandboxProvider";

import { bearCapability } from "./bear-capability";
import { integrationsCapability } from "./integrations-capability";
import { jigWindow } from "./launch";
import { jigOrigin, jigShellUrl } from "./shell";

/** The jig at `path` and its program module for `view`, or null if it has none. */
function jigProgram(playground: Playground, path: string, view: string) {
  const entry = playground.apps.entries().find((e) => e.path === path);
  invariant(entry, `no jig at ${path}`);
  const manifest = entry.manifest;
  if (!manifest) return null;
  const window = jigWindow(entry, view);
  return window?.kind === "program"
    ? { id: entry.id, manifest, module: window.module }
    : null;
}

function launchSpec(
  playground: Playground,
  capabilityProviders: CapabilityProviders,
  path: string,
  view: string,
): AppLaunchSpec | null {
  const program = jigProgram(playground, path, view);
  if (!program) return null;
  const { capabilities } = program.manifest;
  const integrations = normalizeAppIntegrations(capabilities).map((i) => i.id);
  return {
    capabilityProviders: {
      ...capabilityProviders,
      integrations: integrationsCapability(integrations),
    },
    programSpecifier: workspaceProgramSpecifier(path, program.module),
    filesystem: { root: path, grants: [{ path: "/", access: "readwrite" }] },
    permissions: capabilities?.permissions,
    sandbox: capabilities?.sandbox,
  };
}

function shellUrl(
  playground: Playground,
  projectId: string,
  path: string,
  view: string,
): string {
  const program = jigProgram(playground, path, view);
  invariant(program, `jig ${path} has no ${view} program`);
  invariant(program.id, `jig ${path} has no name`);
  return jigShellUrl(
    jigOrigin(new URL(window.location.href), projectId, program.id),
    workspaceProgramPath(path, program.module),
    program.manifest.capabilities?.fetch ?? [],
  );
}

/**
 * Hosts the project's jigs: one persistent iframe per opened jig, kept alive
 * while its pane is hidden (forest's `AppHostRegistry`).
 *
 * HACKY: no policy yet. Every jig gets read/write on the whole project and the
 * network its manifest asks for, with no consent step. Hearth's consent gate and
 * filesystem grants are what replace this.
 */
export function JigsProvider({
  projectId,
  children,
}: {
  projectId: string;
  children: ReactNode;
}) {
  const { playground, run } = useBearSandbox();
  const repo = useRepo();
  const config = useMemo<RegistryHostConfig>(() => {
    const providers = { bear: bearCapability(run) };
    return {
      playground,
      resolveLaunchSpec: (path, view) =>
        launchSpec(playground, providers, path, view),
      publishShell: async (path, _themeUrl, view) =>
        shellUrl(playground, projectId, path, view),
      replica: {
        repo,
        stampFor: playground.stampFor,
        wasm: { automerge: automergeWasmUrl, subduction: subductionWasmUrl },
      },
    };
  }, [playground, projectId, run, repo]);
  return (
    <AppHostRegistryProvider config={config}>
      {children}
    </AppHostRegistryProvider>
  );
}
